import React from 'react';
import { Text, type PillToggleOption } from '@chatsdet/ui';
import type { AssertionSeverity } from '@chatsdet/types';

/**
 * Shared severity options + help blurb for assertion editors (standalone
 * assertion nodes and inline assertions on action nodes). Keep the two
 * editors in sync by importing from here.
 */
export const SEVERITY_OPTIONS: readonly PillToggleOption<AssertionSeverity>[] = [
  {
    value: 'fatal',
    label: 'Fatal',
    activeClassName: 'bg-red-600/20 text-red-700 dark:text-red-300',
  },
  {
    value: 'fail',
    label: 'Fail',
    activeClassName: 'bg-red-500/15 text-red-600 dark:text-red-400',
  },
  {
    value: 'warn',
    label: 'Warn',
    activeClassName: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
];

/** Default severity for newly authored assertions. */
export const DEFAULT_NEW_ASSERTION_SEVERITY: AssertionSeverity = 'fatal';

const SEVERITY_DESCRIPTIONS: Record<AssertionSeverity, string> = {
  fatal:
    'Stops the test immediately. Use when continuing is pointless — e.g. login failed or the page never loaded.',
  fail:
    'Records a failure but keeps running; the run finishes marked failed. Use for independent checks where later steps still provide signal.',
  warn:
    'Notes a problem without failing the run. Use for cosmetic or non-critical checks like toasts.',
};

/** One-line explanation of the currently selected severity. */
export function AssertionSeverityHelp({ severity }: { severity: AssertionSeverity }) {
  return (
    <Text size="xs" className="text-muted-foreground">
      <span className="font-medium">
        {severity === 'fatal' ? 'Fatal' : severity === 'fail' ? 'Fail' : 'Warn'}
      </span>{' '}
      — {SEVERITY_DESCRIPTIONS[severity]}
    </Text>
  );
}
